import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, MultiAccountAccount } from '@/contexts/AuthContext';
import { useClub } from '@/contexts/ClubContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, LogIn, Mail, ShieldCheck } from 'lucide-react';
import { fetchClubsForUser } from '@/lib/clubMembership';
import { setAuthToken } from '@/services/apiClient';
import aivLogo from '@/assets/aiv-logo-trans.png';
import { useI18n } from '@/contexts/I18nContext';

export default function Login() {
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSessionId, setOtpSessionId] = useState<number | null>(null);
  const [isTwoFactorStep, setIsTwoFactorStep] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoginRequired, setFirstLoginRequired] = useState(false);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const [noticeEmail, setNoticeEmail] = useState('');
  const { login, verifyLoginOtp, user, setSessionFromToken } = useAuth();
  const { selectClub } = useClub();
  const navigate = useNavigate();
  const [accountOptions, setAccountOptions] = useState<MultiAccountAccount[] | null>(null);
  
  const proceedAfterLogin = async (accountUser: any) => {
    if (accountUser?.isSuperAdmin) {
      navigate('/super-admin');
      return;
    }
    if (accountUser?.needsConsent) {
      navigate('/consent');
      return;
    }
    const loggedInUserId = accountUser?.id || user?.id;
    try {
      const memberClubs = await fetchClubsForUser(loggedInUserId);
      if (memberClubs.length === 1) {
        await selectClub(memberClubs[0].id);
        navigate('/dashboard');
      } else {
        navigate('/select-club');
      }
    } catch {
      navigate('/select-club');
    }
  };

  const filterAccountsForDisplay = async (accounts: MultiAccountAccount[]) => {
    const filtered: MultiAccountAccount[] = [];
    for (const acc of accounts) {
      const type = (acc.account_type || '').toUpperCase();
      if (type === 'ADMIN') {
        filtered.push(acc);
        continue;
      }

      // For member accounts, only keep if they actually belong to other clubs (not just their own admin club)
      const previousToken = localStorage.getItem('bcm_auth_token');
      setAuthToken(acc.access_token);
      try {
        const clubs = await fetchClubsForUser(acc.id?.toString(), {
          accountType: 'MEMBER',
        });
        if (clubs.length > 0) {
          filtered.push(acc);
        }
      } catch {
        // ignore lookup failures here; we'll just omit the member option
      } finally {
        setAuthToken(previousToken);
      }
    }
    return filtered;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isTwoFactorStep) {
      if (!otpSessionId) {
        toast.error(t('auth_2fa_session_expired', '2FA session expired. Please sign in again.'));
        setIsTwoFactorStep(false);
        return;
      }
      if (otp.trim().length < 4) {
        toast.error(t('auth_enter_otp_prompt', 'Enter the OTP sent to your email'));
        return;
      }

      setIsLoading(true);
      try {
        const verifyResult = await verifyLoginOtp(otpSessionId, otp.trim());
        if (verifyResult.success) {
          toast.success(t('auth_welcome_back_toast', 'Welcome back!'));
          await proceedAfterLogin(verifyResult.user);
          return;
        }
        toast.error(verifyResult.error || t('auth_otp_failed', 'OTP verification failed'));
      } finally {
        setIsLoading(false);
      }
      return;
    }

    setFirstLoginRequired(false);
    setNeedsPasswordReset(false);
    setNoticeEmail('');
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if (result.multiAccount && result.accounts?.length) {
        const filtered = await filterAccountsForDisplay(result.accounts);

        const onlyAdmin = filtered.length === 1 && (filtered[0].account_type || '').toUpperCase() === 'ADMIN';
        if (onlyAdmin) {
          const adminUser = await setSessionFromToken(filtered[0].access_token);
          if (!adminUser) {
            toast.error(t('auth_continue_account_failed', 'Could not continue with that account'));
            return;
          }
          toast.success(t('auth_welcome_back_toast', 'Welcome back!'));
          await proceedAfterLogin(adminUser);
          return;
        }

        if (filtered.length) {
          setAccountOptions(filtered);
          toast.info(t('auth_select_account_continue', 'Select which account to continue with'));
        } else {
          toast.error(t('auth_no_usable_accounts', 'No usable accounts found'));
        }
        return;
      }

      if (result.needsFirstLogin) {
        setFirstLoginRequired(true);
        setNoticeEmail(email);
        toast.info(t('auth_first_login_check_email', 'Check your email for your first-time setup link to set your password.'));
        return;
      }

      if (result.needsPasswordReset) {
        setNeedsPasswordReset(true);
        setNoticeEmail(email);
        toast.info(t('auth_reset_required_info', 'Your password expired. Request a reset link to continue.'));
        return;
      }
      if (result.requires2fa) {
        setIsTwoFactorStep(true);
        setOtpSessionId(result.otpSessionId ?? null);
        setOtp('');
        setNoticeEmail(email);
        toast.info(t('auth_enter_otp_to_complete', 'Enter the OTP sent to your email to complete login.'));
        return;
      }

      if (result.success) {
        setFirstLoginRequired(false);
        setNeedsPasswordReset(false);
        setNoticeEmail('');
        toast.success(t('auth_welcome_back_toast', 'Welcome back!'));
        await proceedAfterLogin(result.user);
      } else {
        toast.error(result.error || t('auth_login_failed', 'Login failed'));
        setNeedsPasswordReset(false);
        setFirstLoginRequired(false);
        setIsTwoFactorStep(false);
        setOtpSessionId(null);
        setOtp('');
      }
    } catch (error) {
      toast.error(t('common_error_occurred', 'An error occurred'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <div className="w-full max-w-md md:max-w-3xl lg:max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center rounded-2xl text-primary-foreground mb-4">
            <img style={{height:"130px", width:"130px"}} src={aivLogo} alt="AIV Logo" className="object-contain" />
          </div>
          <h1 className="text-4xl font-display font-bold text-foreground">SmashLytics</h1>
          <p className="text-muted-foreground mt-2">{t('login_brand_subtitle', 'Badminton Club')}</p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl md:text-4xl font-display">{t('login_welcome_back', 'Welcome back')}</CardTitle>
            <CardDescription className="text-sm md:text-lg">{t('login_subtitle', 'Enter your credentials to access your account')}</CardDescription>
          </CardHeader>
          <CardContent>
            {firstLoginRequired && (
              <Alert className="mb-4 border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-500/40 dark:bg-blue-900/20">
                <AlertTitle className="flex items-center gap-2">
                  <Mail size={18} /> {t('auth_account_setup_required', 'Account setup required')}
                </AlertTitle>
                <AlertDescription className="space-y-1">
                  <p>
                    {t('auth_first_time_link_sent', 'We sent a first-time setup link to')} {noticeEmail || t('signup_email', 'Email').toLowerCase()}. {t('auth_finish_setup_before_signin', 'Open that email and finish setting your password before signing in.')}
                  </p>
                </AlertDescription>
              </Alert>
            )}
            {needsPasswordReset && (
              <Alert className="mb-4 border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-900/20">
                <AlertTitle className="flex items-center gap-2">
                  <Mail size={18} /> {t('auth_password_reset_required', 'Password reset required')}
                </AlertTitle>
                <AlertDescription className="space-y-2">
                  <p>
                    {t('auth_password_expired_use_reset', 'Your password has expired. Use the reset link sent to')} {noticeEmail || t('signup_email', 'Email').toLowerCase()} {t('auth_password_expired_suffix', 'to set a new password.')}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-1"
                    onClick={() => navigate('/forgot-password', { state: { email: noticeEmail || email } })}
                  >
                    {t('auth_request_new_reset_link', 'Request new reset link')}
                  </Button>
                </AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              {!isTwoFactorStep ? (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-base md:text-lg">{t('login_email', 'Email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-11 md:h-14 text-base md:text-lg"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-base md:text-lg">{t('login_password', 'Password')}</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="********"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="h-11 md:h-14 text-base md:text-lg pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="space-y-4 rounded-md border border-primary/30 bg-primary/5 p-4">
                  <p className="text-sm md:text-base flex items-center gap-2">
                    <ShieldCheck size={18} className="text-primary" />
                    {t('auth_2fa_enabled', 'Two-factor authentication is enabled for this account.')}
                  </p>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {t('auth_enter_otp_sent_to', 'Enter the OTP sent to')} {noticeEmail || email}.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="otp" className="text-base md:text-lg">{t('auth_otp_code', 'OTP Code')}</Label>
                    <Input
                      id="otp"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder={t('auth_enter_code', 'Enter code')}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="h-11 md:h-14 tracking-widest text-center text-lg md:text-xl"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsTwoFactorStep(false);
                        setOtp('');
                        setOtpSessionId(null);
                      }}
                      disabled={isLoading}
                    >
                      {t('common_back', 'Back')}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={async () => {
                        setIsLoading(true);
                        try {
                          const resendResult = await login(email, password);
                          if (resendResult.requires2fa && resendResult.otpSessionId) {
                            setOtpSessionId(resendResult.otpSessionId);
                            setOtp('');
                            toast.success(t('auth_otp_resent', 'OTP resent to your email.'));
                            return;
                          }
                          toast.error(resendResult.error || t('auth_resend_otp_failed', 'Unable to resend OTP'));
                        } finally {
                          setIsLoading(false);
                        }
                      }}
                      disabled={isLoading}
                    >
                      {t('login_resend_otp', 'Resend OTP')}
                    </Button>
                  </div>
                </div>
              )}

              <Button type="submit" className="w-full h-11 md:h-14 text-base md:text-lg" disabled={isLoading}>
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> {isTwoFactorStep ? t('login_verifying_otp', 'Verifying OTP...') : t('login_signing_in', 'Signing in...')}
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <LogIn size={18} /> {isTwoFactorStep ? t('login_verify_sign_in', 'Verify and Sign In') : t('login_sign_in', 'Sign in')}
                  </span>
                )}
              </Button>
            </form>

            {!isTwoFactorStep && (
              <div className="mt-6 text-center text-sm">
                <Link
                  to="/forgot-password"
                  state={{ email }}
                  className="text-primary hover:underline font-medium"
                >
                  {t('login_forgot_password', 'Forgot password?')}
                </Link>
              </div>
            )}

            {accountOptions && accountOptions.length > 0 && (
              <div className="mt-6 border rounded-lg p-3 bg-muted/40">
                <p className="text-sm font-medium text-foreground mb-2">{t('login_choose_continue', 'Choose how to continue')}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {accountOptions.map((opt, idx) => {
                    const roleLabel = (opt.account_type || opt.role || '').toString().toUpperCase();
                    return (
                      <Button
                        key={`${roleLabel}-${idx}`}
                        variant="outline"
                        className="justify-start h-auto py-3 px-3 text-left"
                        onClick={async () => {
                          setIsLoading(true);
                          try {
                            const mappedUser = await setSessionFromToken(opt.access_token);
                            if (mappedUser?.needsConsent) {
                              navigate('/consent');
                              return;
                            }
                            const accountType = (opt.account_type || '').toUpperCase() || 'MEMBER';
                            const userId = mappedUser?.id;
                            const clubs = await fetchClubsForUser(userId, {
                              accountType,
                              skipMemberEnumeration: accountType === 'ADMIN',
                            });
                            if (accountType === 'ADMIN') {
                              if (clubs.length === 0) {
                                navigate('/dashboard');
                              } else if (clubs.length === 1) {
                                await selectClub(clubs[0].id);
                                navigate('/dashboard');
                              } else {
                                navigate('/select-club');
                              }
                            } else {
                              if (clubs.length === 1) {
                                await selectClub(clubs[0].id);
                                navigate('/dashboard');
                              } else if (clubs.length > 1) {
                                navigate('/select-club');
                              } else {
                                toast.info(t('auth_not_in_club_yet', 'You are not in a club yet. Ask your admin to invite you.'));
                                navigate('/select-club');
                              }
                            }
                          } catch (err) {
                            toast.error(t('auth_continue_account_failed', 'Could not continue with that account'));
                          } finally {
                            setIsLoading(false);
                          }
                        }}
                      >
                        <div className="flex flex-col text-sm">
                          <span className="font-semibold">{roleLabel === 'ADMIN' ? t('login_admin_account', 'Admin account') : t('login_member_account', 'Member account')}</span>
                          {opt.first_name || opt.last_name ? (
                            <span className="text-xs text-muted-foreground">
                              {(opt.first_name || '')} {(opt.last_name || '')}
                            </span>
                          ) : null}
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* <div className="mt-3 text-center text-sm">
              <span className="text-muted-foreground">{t('login_no_account', "Don't have an account?")} </span>
              <Link to="/signup" className="text-primary hover:underline font-medium">
                {t('login_create_one_here', 'Create one here')}
              </Link>
            </div> */}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}


